import React, { useState } from 'react';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { MyDocumentsApi } from '../../api/myDocumentsApi';
import { useDispatch } from 'react-redux';
import { setUpdateMyDocumentsList } from 'reducers/misc';
import instance, { ApiResponse, useFetch } from 'api';
import { endpoints } from 'endpoints.config';
import warningIcon from '../../images/Icon_Warning_01.png';
import { Spinner } from 'components/spinner/Spinner';
import { GetDeleteFoldersResponse } from './DeleteFolder';

type FailedDeletion = {
  id: number;
  reason: string;
};

type DeletionResponse = {
  successfulDeletions: number[];
  failedDeletions: FailedDeletion[];
};

type ErrorDeletion = {
  hasError: boolean;
  failedDeletions: FailedDeletion[];
};
type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedData: any[];
};

export const DeleteAll: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  selectedData,
}) => {
  const dispatch = useDispatch();
  const folderIds: number[] = [];
  const fileIds: number[] = [];
  selectedData.forEach((d) => {
    if (d.documents__id) {
      fileIds.push(d.documents__id);
    } else if (d.folders__id) {
      folderIds.push(d.folders__id);
    }
  });

  const { loading, data, error } = useFetch<GetDeleteFoldersResponse>(
    endpoints.myDocuments.deleteFolders,
    {
      paramsSerializer: (params) => {
        return folderIds.map((id) => `foldersIds=${id}`).join('&');
      },
      params: { foldersIds: folderIds },
    }
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorState, setErrorState] = useState<ErrorDeletion | undefined>();

  const deleteFolder = () => {
    const payload = { idsToDelete: folderIds };
    return instance
      .post<ApiResponse<any, DeletionResponse>>(
        endpoints.myDocuments.deleteFolders,
        payload
      )
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        // Generic error
        setErrorState({ hasError: true, failedDeletions: [] });
      });
  };

  const deleteFile = async () => {
    const result = await MyDocumentsApi.deleteFile(fileIds);

    if (result.data) {
      return result.data;
    }

    if (result.error) {
      setErrorState({ hasError: true, failedDeletions: [] });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorState({ hasError: false, failedDeletions: [] });
    let hasError = false;
    if (folderIds.length > 0) {
      const folderResponse = await deleteFolder();
      if (folderResponse?.details?.failedDeletions?.length > 0) {
        hasError = true;
        setErrorState({
          hasError: true,
          failedDeletions: folderResponse?.details.failedDeletions ?? [],
        });
      }
    }

    if (fileIds.length > 0) {
      const fileResponse = await deleteFile();
      if (fileResponse?.details?.failedDeletions?.length > 0) {
        hasError = true;
        setErrorState({
          hasError: true,
          failedDeletions: fileResponse.details.failedDeletions,
        });
      }
    }

    if (!hasError) {
      dispatch(setUpdateMyDocumentsList(true));
      togglePanelOpen(false);
    }

    setSubmitting(false);
  };

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={panelOpen}
      hdrTitle="Delete folders/files"
      position="right"
      primaryBtnTxt="Delete"
      secondaryBtnTxt="Cancel"
      givenClass="DeleteDocument"
      primaryBtnColor="BtnRed"
      handleClose={() => togglePanelOpen(false)}
      handlePrimaryBtn={handleSubmit}
      handleSecondaryBtn={() => togglePanelOpen(false)}
      width={560}
      isSubmitting={submitting}
      noOverflowToggle={true}
    >
      <div className="DeleteModal">
        <h4>Permanently Delete</h4>

        <p>Are you sure you want to permanently delete these folders/files?</p>
        <div className="LineSpacer3"></div>
        <p className="BoldText">
          You'll permanently lose all information about these folders/files
        </p>
        <div className="DocDeleteList">
          {selectedData?.map((item, index) => (
            <div
              className="DLItem"
              key={index + (item.folders__id ?? item.documents__id)}
            >
              -{item.list__itemname}
            </div>
          ))}
        </div>
        {loading && !data ? (
          <Spinner positionAbsolute />
        ) : data?.details.foldersAndDocumentsToDelete &&
          data?.details.foldersAndDocumentsToDelete.length > 0 ? (
          <>
            <p className="BoldText">
              There contents will also be permanently deleted:
            </p>
            <div className="DocDeleteList">
              {data?.details.foldersAndDocumentsToDelete.map((d) => (
                <div className="DLItem" key={d.folderAndDocument}>
                  -{d.folderAndDocument}
                </div>
              ))}
            </div>
          </>
        ) : null}
        <div className="DeleteModalWarning">
          <img src={warningIcon} alt="warning" />
          <div style={{ alignContent: 'center' }}>
            <p>This action cannot be undone</p>
          </div>
        </div>
        {errorState?.hasError && (
          <div className="ErrorTxt">
            {errorState.failedDeletions.length > 0
              ? `Error deleting the following folders:`
              : `Error deleting the following folder:`}
            <ul>
              {errorState.failedDeletions.map((d, i) => (
                <li key={i}>
                  {d.id}: {d.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PopupOrPanel>
  );
};
